import CompanyLayout from '@/components/feature/CompanyLayout';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

function MetricCard({ value, label }: { value: string; label: string }) {
  const { isDark } = useTheme();
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#888]' : 'text-[#666]';
  const bgCard = isDark ? 'bg-[#0a0a0a]' : 'bg-white';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  return (
    <div className={`p-5 rounded-xl border ${borderLight} ${bgCard} text-center`}>
      <div className={`text-2xl md:text-3xl font-bold ${textPrimary} mb-1`}>{value}</div>
      <div className={`text-xs uppercase tracking-wider ${textSecondary}`}>{label}</div>
    </div>
  );
}

export default function AboutPage() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#888]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#555]' : 'text-[#999]';
  const bgCard = isDark ? 'bg-[#0a0a0a]' : 'bg-white';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  const values = [
    {
      title: t('about.decentralization', 'Decentralization'),
      desc: t('about.decentralizationDesc', 'We believe in a world where financial markets operate without intermediaries. Every trade on ZhiSu is settled on-chain, giving users full custody of their assets at all times.'),
      icon: 'ri-global-line',
    },
    {
      title: t('about.innovation', 'Innovation'),
      desc: t('about.innovationDesc', 'We push the boundaries of what is possible in DeFi. From AI-powered trading agents to cross-chain liquidity aggregation, we are building the infrastructure for the next generation of finance.'),
      icon: 'ri-lightbulb-flash-line',
    },
    {
      title: t('about.transparency', 'Transparency'),
      desc: t('about.transparencyDesc', 'All protocol operations, fee structures, and governance decisions are open and auditable. Our smart contracts are publicly verified, and our roadmap is shared with the community.'),
      icon: 'ri-eye-line',
    },
    {
      title: t('about.security', 'Security'),
      desc: t('about.securityDesc', 'Security is not an afterthought. Our code undergoes multiple independent audits, and we operate a comprehensive bug bounty program to ensure the safety of user funds.'),
      icon: 'ri-shield-check-line',
    },
  ];

  const team = [
    { name: 'Alex Chen', role: t('about.ceo', 'Chief Executive Officer'), initials: 'AC' },
    { name: 'Sarah Nakamoto', role: t('about.cto', 'Chief Technology Officer'), initials: 'SN' },
    { name: 'Marcus Williams', role: t('about.headProduct', 'Head of Product'), initials: 'MW' },
    { name: 'Yuki Tanaka', role: t('about.headAI', 'Head of AI Research'), initials: 'YT' },
    { name: 'Elena Petrova', role: t('about.clo', 'Chief Legal Officer'), initials: 'EP' },
    { name: "James O'Connor", role: t('about.headEng', 'Head of Engineering'), initials: 'JO' },
  ];

  const investors = [
    'Polychain Capital',
    'Jump Crypto',
    'Dragonfly Capital',
    'Framework Ventures',
    'Multicoin Capital',
    'Electric Capital',
    'Pantera Capital',
    'Hashed',
  ];

  return (
    <CompanyLayout
      title={t('about.title', 'About ZhiSu')}
      subtitle={t('about.subtitle', 'Building the future of decentralized derivatives trading')}
    >
      {/* Hero Section */}
      <div className={`p-6 md:p-8 rounded-xl border ${borderLight} ${bgCard} mb-8`}>
        <p className={`leading-relaxed mb-6 ${textSecondary}`}>
          {t('about.founded', 'Founded in 2024, ZhiSu is a next-generation decentralized derivatives exchange built on cutting-edge blockchain infrastructure. Our mission is to democratize access to sophisticated financial instruments by combining the speed and efficiency of centralized exchanges with the security and transparency of decentralized protocols.')}
        </p>
        <p className={`leading-relaxed mb-6 ${textSecondary}`}>
          {t('about.volume', 'With over $2.8 billion in cumulative trading volume and a growing ecosystem of AI-powered trading agents, ZhiSu has become one of the most trusted platforms in the DeFi space. We serve traders from over 120 countries, offering perpetual futures on 50+ cryptocurrency pairs with up to 50x leverage.')}
        </p>
        <p className={`leading-relaxed ${textSecondary}`}>
          {t('about.teamDesc', 'Our team brings together expertise from leading quantitative hedge funds, traditional finance institutions, and top blockchain protocols. We are backed by some of the most respected investors in the crypto industry and are committed to building infrastructure that will power the future of global finance.')}
        </p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <MetricCard value="$2.8B+" label={t('about.totalVolume', 'Total Volume')} />
        <MetricCard value="145K+" label={t('about.activeTraders', 'Active Traders')} />
        <MetricCard value="50+" label={t('about.markets', 'Markets')} />
        <MetricCard value="120+" label={t('about.countries', 'Countries')} />
      </div>

      {/* Values */}
      <div className={`p-6 md:p-8 rounded-xl border ${borderLight} ${bgCard} mb-8`}>
        <h2 className={`text-xl font-bold mb-6 ${textPrimary}`}>{t('about.ourValues', 'Our Values')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {values.map((v) => (
            <div key={v.title} className="flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#00ff88]/10 flex items-center justify-center flex-shrink-0">
                <i className={`${v.icon} text-[#00ff88] w-5 h-5 flex items-center justify-center`} />
              </div>
              <div>
                <h3 className={`font-semibold mb-1 ${textPrimary}`}>{v.title}</h3>
                <p className={`text-sm leading-relaxed ${textSecondary}`}>{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Team */}
      <div className={`p-6 md:p-8 rounded-xl border ${borderLight} ${bgCard} mb-8`}>
        <h2 className={`text-xl font-bold mb-6 ${textPrimary}`}>{t('about.leadershipTeam', 'Leadership Team')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {team.map((member) => (
            <div key={member.name} className={`p-4 rounded-lg border ${borderLight} text-center`}>
              <div className="w-14 h-14 rounded-full bg-[#00ff88]/10 flex items-center justify-center mx-auto mb-3">
                <span className="text-[#00ff88] font-bold text-sm">{member.initials}</span>
              </div>
              <h3 className={`font-semibold text-sm ${textPrimary}`}>{member.name}</h3>
              <p className={`text-xs mt-1 ${textMuted}`}>{member.role}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Investors */}
      <div className={`p-6 md:p-8 rounded-xl border ${borderLight} ${bgCard}`}>
        <h2 className={`text-xl font-bold mb-6 ${textPrimary}`}>{t('about.backedBy', 'Backed By')}</h2>
        <div className="flex flex-wrap gap-3">
          {investors.map((inv) => (
            <span
              key={inv}
              className={`px-4 py-2 rounded-lg border ${borderLight} text-sm ${textSecondary}`}
            >
              {inv}
            </span>
          ))}
        </div>
      </div>
    </CompanyLayout>
  );
}